/* Charger finder on the home page (sections/zvh-finder.liquid).

   DATA FLOWS (same as the finder it replaces, sections/zev-charger-finder.liquid)
   - Cars: the EV catalogue, printed into the section from the shop metafield
     zev.finder_cars (DataVault rewrites it seconds after a car is saved,
     ev_catalog_module/finder_cars_sync.py), then refreshed at runtime from
     the public feed (widget.zevpoint.com/api/public/plan/cars). Both carry
     the same rows; no car is typed in the theme.
   - Chargers: the products of the "Charger finder" collection, each with
     its own name, power, supply, kind, one-line description, price, link and
     picture. Nothing about a charger is typed in the section or this file.
   - Misses: searches with no result, and taps on the WhatsApp fallback, are
     beaconed to /api/public/finder/miss; usage goes to /api/public/finder/event.
   - Sales links: /?ev=<slug> opens the finder with that car chosen. No car is
     ever pre-selected otherwise.
   - Orders: a tap on a charger in the result writes the hidden cart attribute
     `_finder` ("car | supply | product handle (top pick / other row) |
     label"); Shopify keeps it on the
     order, so orders that started in the finder can be counted.

   RECOMMENDATION: among the chargers the car can actually use on the chosen
   supply (ceiling `m`, ev_compatibility; a car without one falls back
   to the power class, charger kW − car kW < 3), the one owners of that
   car buy most. The feed sends that order as `top` (Shopify product ids,
   counted from invoices by DataVault, ev_catalog_module/charger_popularity)
   and `top_by`: 'car' when the car has enough sales of its own, 'class' when
   it borrows every car of its ceiling. A block ticked "Feature this charger"
   goes first; a sold-out product is left out; with no count the dearest
   leads, as before. Under the pick: the runner-up, then the best of
   another kind, each described by what it is (the pick carries the only
   claim). When a kind is asked for and fewer than three of it fit, the rows
   left go to the best of the other kinds, so the box is never empty. When single phase holds the car below its own speed, the headline
   and the label say the speed it will get, not the car's. */
(function () {
  var MISS_API = 'https://widget.zevpoint.com/api/public/finder/miss';
  var EVENT_API = 'https://widget.zevpoint.com/api/public/finder/event';
  var CARS_FEED = 'https://widget.zevpoint.com/api/public/plan/cars';
  var SUPPLY_KW = { single_phase: 7.5, three_phase: 22 };
  var DERATE = { 11: 3.6, 22: 7.5 };
  var POWER_CLASS_KW = 3;
  var TYPE = { wall: 'Wall-mounted', portable: 'Portable', aio: 'All-in-one' };
  var KIND = { wall: 'wall charger', portable: 'portable', aio: 'all-in-one' };
  var LONG_NAME = 20;
  var PREF_ORDER = ['Tata Nexon EV LR', 'Tata Nexon EV MR', 'Tata Punch EV', 'MG Windsor EV', 'Mahindra BE 6', 'Hyundai Creta Electric', 'Tata Curvv EV', 'MG Comet EV', 'BYD Atto 3'];
  /* Sales links sent before cars were merged or renamed (Sep 2026). */
  var SLUG_ALIASES = {
    'tata-nexon-ev-7-2-kw': 'tata-nexon-ev-lr', 'tata-nexon-ev-6-6-kw': 'tata-nexon-ev-lr',
    'tata-nexon-ev-max': 'tata-nexon-ev-lr', 'tata-nexon-ev-30-mr': 'tata-nexon-ev-mr',
    'tata-nexon-ev-45': 'tata-nexon-ev-lr', 'tata-nexon-ev-45-7-2-kw-ac': 'tata-nexon-ev-lr',
    'tata-tiago-ev-7-2-kw-ac': 'tata-tiago-ev-7-5-kw-ac',
    'hyundai-creta': 'hyundai-creta-electric', 'hyundai-creta-51-4': 'hyundai-creta-electric'
  };
  var EZ = 'cubic-bezier(.22,.8,.26,1)';

  function inr(n) { return 'Rs. ' + Math.round(n).toLocaleString('en-IN'); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function slugify(name) { return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }

  /* ---- car search: token by token, with typo tolerance by token length ---- */
  function normText(s) {
    s = (s || '').toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function squash(s) { return normText(s).replace(/ /g, ''); }
  /* "cc3" -> "c3": collapse one doubled keystroke, never a longer run */
  function dedupe(s) { return s.replace(/(.)\1+/g, function (run, ch) { return run.length === 2 ? ch : run; }); }
  function editDist(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      for (j = 0; j <= b.length; j++) prev[j] = cur[j];
    }
    return prev[b.length];
  }
  function slack(n) { return n >= 6 ? 2 : (n >= 4 ? 1 : 0); }
  /* trim keywords carry parsing debris ("kwh", "15a", "2021") that matches everything */
  var K_STOP = ' kwh type kw ac dc current est batt ev ';
  function usefulTrim(w) {
    if (!w || w.length < 3) return false;
    if (K_STOP.indexOf(' ' + w + ' ') !== -1) return false;
    return !/^[0-9]/.test(w);
  }
  function carTokens(c) {
    if (c._t) return c._t;
    var toks = normText(c.n).split(' ');
    if (c.k) toks = toks.concat(normText(c.k).split(' ').filter(usefulTrim));
    var extra = [];
    toks.forEach(function (w) {
      /* buyers drop the leading "e": eC3 gets typed "c3", EX30 "x30" */
      if (w.length >= 3 && w.length <= 5 && w.charAt(0) === 'e') extra.push(w.slice(1));
    });
    c._t = { toks: toks.concat(extra).filter(Boolean), sq: squash(c.n + ' ' + (c.k || '')), nsq: squash(c.n) };
    return c._t;
  }
  /* one query token against one car; a guess always scores below a real hit */
  function tokenScore(c, t) {
    var d = carTokens(c), toks = d.toks, i, w, td = dedupe(t);
    for (i = 0; i < toks.length; i++) {
      w = toks[i];
      if (w === t || w === td) return 100;
      if (w.indexOf(t) === 0 || w.indexOf(td) === 0) return 80;
    }
    if (t.length >= 3) {
      if (d.sq.indexOf(t) === 0) return 70;
      if (d.sq.indexOf(t) !== -1) return 50;
    }
    var s = slack(t.length);
    if (s > 0) {
      for (i = 0; i < toks.length; i++) {
        w = toks[i];
        if (w.charAt(0) !== t.charAt(0)) continue;
        if (w.length > t.length && editDist(t, w.slice(0, t.length), s) <= s) return 60;
      }
      for (i = 0; i < toks.length; i++) {
        w = toks[i];
        if (w.charAt(0) !== t.charAt(0)) continue;
        if (editDist(t, w, s) <= s) return 30;
      }
    }
    return 0;
  }
  /* Two-wheelers get a straight answer instead of the WhatsApp fallback. */
  var TWO_WHEELER = [
    'scooter', 'scooty', 'twowheeler', 'motorcycle', 'moped', 'bike', 'ebike',
    'ola', 'olaelectric', 'tvs', 'iqube', 'orbiter', 'hero', 'heroelectric',
    'vida', 'bajaj', 'chetak', 'ather', 'rizta', 'okinawa', 'ampere', 'greaves',
    'revolt', 'okaya', 'bgauss', 'pureev', 'battre', 'komaki', 'benling',
    'gemopai', 'odysse', 'lectrix', 'avon', 'joy', 'wardwizard', 'bounce',
    'ultraviolette', 'simpleenergy', 'oben', 'tork', 'kratos', 'matter',
    'aera', 'river', 'indie', 'raptee', 'kabira', 'hop', 'ninetyone',
    'techoelectra', 'eider', 'zelio', 'ampereprimus', 'quantum', 'evolet',
    'emotorad', 'lectro', 'yulu', 'motovolt', 'ninety'
  ];
  function isTwoWheeler(q) {
    var raw = normText(q);
    if (!raw) return false;
    var toks = raw.split(' '), flat = raw.replace(/ /g, ''), i, j, t, w;
    for (j = 0; j < TWO_WHEELER.length; j++) if (TWO_WHEELER[j] === flat) return true;
    for (i = 0; i < toks.length; i++) {
      t = toks[i];
      if (t.length < 3) continue;
      for (j = 0; j < TWO_WHEELER.length; j++) {
        w = TWO_WHEELER[j];
        if (w === t || w.indexOf(t) === 0) return true;
      }
    }
    return false;
  }

  function init(root) {
    var D;
    try { D = JSON.parse(root.querySelector('.zvh-finder__data').textContent); } catch (e) { return; }
    var CHARGERS = (D.chargers || []).map(function (c) { c.kw = parseFloat(c.kw) || 0; return c; }).filter(function (c) { return c.kw && c.url; });
    var CARS = D.cars || [];
    ['examples', 'popular', 'brands'].forEach(function (k) {
      D[k] = (D[k] || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    });
    var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var input = root.querySelector('.zvh-finder__input');
    var ph = root.querySelector('.zvh-finder__ph');
    var phName = ph && ph.querySelector('b');
    var bar = root.querySelector('.zvh-finder__bar');
    var goBtn = root.querySelector('.zvh-finder__go');
    var panel = root.querySelector('.zvh-finder__panel');
    var popRow = root.querySelector('.zvh-finder__pop');
    var result = root.querySelector('.zvh-finder__result');
    if (!input || !panel || !result) return;

    var state = { car: null, supply: 'single_phase', pref: '' };
    var list = [], act = 0, closeTimer = null;

    function findCar(name) { return CARS.filter(function (c) { return c.n === name; })[0] || null; }
    function popularCars() {
      return CARS.filter(function (c) { return c.p; }).sort(function (a, b) {
        var ia = PREF_ORDER.indexOf(a.n), ib = PREF_ORDER.indexOf(b.n);
        if (ia === -1) ia = 99;
        if (ib === -1) ib = 99;
        return ia - ib || a.n.localeCompare(b.n);
      });
    }
    function matches(q) {
      var raw = normText(q);
      if (!raw) return (q && q.trim()) ? [] : popularCars().slice(0, 6);
      if (isTwoWheeler(raw)) return [];
      var qt = raw.split(' '), sqq = raw.replace(/ /g, ''), scored = [];
      /* a make's first word ("tata", "mg") never vouches for an unknown word next to it */
      var makes = {};
      CARS.forEach(function (c) { makes[normText(c.n).split(' ')[0]] = 1; });
      CARS.forEach(function (c) {
        var total = 0, missed = 0, strong = 0, exact = 0, unknown = 0, named = false, i, t, s;
        for (i = 0; i < qt.length; i++) {
          t = qt[i];
          s = tokenScore(c, t);
          if (s) {
            total += s;
            if (t.length >= 3) strong++;
            if (s === 100) { exact++; if (!makes[t] && (t.length >= 3 || /[0-9]/.test(t))) named = true; }
            continue;
          }
          /* a bare number or a short scrap is a variant hint ("EQS 580") */
          if (/^[0-9]+$/.test(t) || t.length <= 2) { missed++; continue; }
          /* one unknown word ("xuv 3xo", "nexon eviv3") is let through below,
             only when another word names this car exactly */
          if (++unknown > 1) { missed = 999; break; }
        }
        if (missed >= 999 || total === 0) return;
        if (unknown && !named) return;
        total -= unknown * 40;
        if (strong === 0 && exact === 0) return;
        var nsq = carTokens(c).nsq;
        if (nsq.indexOf(sqq) === 0) total += 200;
        else if (nsq.indexOf(sqq) !== -1) total += 120;
        if (c.p) total += 15;
        total -= missed * 5;
        total -= c.n.length * 0.1;
        scored.push({ c: c, s: total });
      });
      scored.sort(function (a, b) { return b.s - a.s || a.c.n.localeCompare(b.c.n); });
      return scored.slice(0, 7).map(function (r) { return r.c; });
    }

    /* ---- telemetry ---- */
    var reportedMisses = {}, missTimer = null;
    function reportMiss(q, context) {
      q = (q || '').trim();
      if (q.length < 3) return;
      var key = context + ':' + q.toLowerCase();
      if (reportedMisses[key]) return;
      reportedMisses[key] = true;
      zva('finder_miss', { q: q, context: context });
      try {
        fetch(MISS_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ q: q, context: context }), keepalive: true }).catch(function () {});
      } catch (e) {}
    }
    var sid = '';
    try {
      sid = localStorage.getItem('zev_cf_sid') || '';
      if (!sid) {
        sid = Array.from(crypto.getRandomValues(new Uint8Array(8))).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
        localStorage.setItem('zev_cf_sid', sid);
      }
    } catch (e) {}
    var evtCount = 0, lastResultKey = '';
    /* the same moments, for Marketing -> Website in DataVault (snippets/zev-analytics.liquid) */
    var zvaSeen = {};
    function zva(name, fields, handle) {
      try { if (window.zva) window.zva.track(name, fields, handle); } catch (e) {}
    }
    function zvaOnce(name, fields) {
      if (zvaSeen[name]) return;
      zvaSeen[name] = true;
      zva(name, fields);
    }
    function sendEvent(name, fields) {
      if (evtCount >= 60) return;
      evtCount++;
      var payload = fields || {};
      payload.e = name;
      payload.sid = sid;
      zva(name === 'car_selected' ? 'finder_car' : 'finder_' + name, fields);
      try {
        fetch(EVENT_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), keepalive: true }).catch(function () {});
      } catch (e) {}
    }
    /* mark the cart: the order then says it started here (hidden at checkout) */
    function markCart(a, isPick) {
      var m = state.car && model(state.car);
      zva('finder_click', { car: state.car && state.car.n, pick: isPick, supply: state.supply }, handleFromUrl(a.href));
      var v = [state.car && state.car.n, state.supply === 'three_phase' ? 'three phase' : 'single phase',
               handleFromUrl(a.href) + (isPick ? ' (top pick)' : ' (other row)'), m && m.label].filter(Boolean).join(' | ');
      try {
        fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _finder: v } }), keepalive: true, credentials: 'same-origin' }).catch(function () {});
      } catch (e) {}
    }
    function handleFromUrl(u) {
      try { return (u || '').split('?')[0].split('/').filter(Boolean).pop() || ''; } catch (e) { return ''; }
    }

    /* ---- the box: rotating example, popular cars ---- */
    function onPhone() { return window.matchMedia('(max-width: 749px)').matches; }
    /* phones have no room for the lead words: they show the instruction
       (data-hint) first, then each example on its own; -1 is the instruction */
    var phNames = D.examples || [], phHint = (ph && ph.getAttribute('data-hint')) || '', phI = -2;
    function phShow() {
      var hint = phI < 0;
      var b = phName.cloneNode(false);
      b.textContent = hint ? phHint : phNames[phI];
      b.classList.toggle('is-hint', hint);
      phName.parentNode.replaceChild(b, phName);   /* a new node replays the fade-in */
      phName = b;
    }
    function phNext() {
      phI = phI + 1 >= phNames.length ? -1 : phI + 1;
      if (phI < 0 && !(phHint && onPhone())) phI = 0;
    }
    if (phName && phNames.length) {
      phNext();
      phShow();
      setInterval(function () {
        if (input.value || state.car) return;
        phNext();
        phShow();
      }, 2400);
    }
    function syncPlaceholder() { if (ph) ph.style.display = input.value ? 'none' : ''; }
    function renderPopular() {
      if (!popRow) return;
      var cars = (D.popular || []).map(findCar).filter(Boolean);
      popRow.hidden = !cars.length;
      Array.prototype.slice.call(popRow.querySelectorAll('button')).forEach(function (b) { b.remove(); });
      cars.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = c.n.replace(/^(Tata|Mahindra|Hyundai|MG) /, '');
        b.addEventListener('click', function () { choose(c); });
        popRow.appendChild(b);
      });
    }

    /* ---- the list under the box ---- */
    function highlight(name, q) {
      q = (q || '').trim().toLowerCase();
      if (!q) return esc(name);
      var low = name.toLowerCase(), i = low.indexOf(q), len = q.length;
      if (i < 0) { var t = q.split(/\s+/)[0]; i = low.indexOf(t); len = t.length; }
      if (i < 0 || !len) return esc(name);
      return esc(name.slice(0, i)) + '<mark>' + esc(name.slice(i, i + len)) + '</mark>' + esc(name.slice(i + len));
    }
    function setActive(i) {
      act = Math.max(0, Math.min(list.length - 1, i));
      var rows = panel.querySelectorAll('.zvh-finder__opt');
      Array.prototype.forEach.call(rows, function (r, k) { r.classList.toggle('is-active', k === act); });
      var hl = panel.querySelector('.zvh-finder__hl');
      if (hl) hl.style.setProperty('--i', act);
    }
    function renderPanel() {
      var q = input.value, has = !!q.trim(), html = '', phone = onPhone();
      /* phones: nothing until a letter is typed, then a short list that
         stays above the keyboard */
      if (phone && !has) { closePanel(); return; }
      list = matches(q);
      if (phone) list = list.slice(0, 5);
      act = 0;
      clearTimeout(missTimer);
      if (!has && D.brands && D.brands.length) {
        html += '<div class="zvh-finder__cap zvh-finder__cap--mint">Browse by carmaker</div><div class="zvh-finder__brands">';
        D.brands.forEach(function (b) { html += '<button type="button" data-brand="' + esc(b) + '">' + esc(b) + '</button>'; });
        html += '</div><div class="zvh-finder__rule"></div>';
      }
      var label = has ? (list.length ? (list.length === 1 ? '1 match' : list.length + ' matches') : '') : 'Popular in India';
      if (label && !phone) html += '<div class="zvh-finder__cap">' + label + '</div>';
      html += '<div class="zvh-finder__list" role="listbox">';
      if (list.length) html += '<div class="zvh-finder__hl" aria-hidden="true"></div>';
      list.forEach(function (c, i) {
        html += '<button type="button" class="zvh-finder__opt' + (i === 0 ? ' is-active' : '') + '" role="option" data-i="' + i + '">'
          + '<span><b>' + highlight(c.n, q) + '</b><small>' + esc(c.b) + ' kWh battery · ' + esc(c.o) + ' kW AC</small></span>'
          + '<i aria-hidden="true">→</i></button>';
      });
      html += '</div>';
      if (has && !list.length) {
        if (isTwoWheeler(q)) {
          /* tagged apart so two-wheeler demand does not read as a gap in the car catalogue */
          missTimer = setTimeout(function () { reportMiss(q, 'two_wheeler'); }, 900);
          html += '<div class="zvh-finder__none">Currently Zevpoint chargers are compatible with electric cars only. We hope to serve you in the near future!</div>';
        } else {
          missTimer = setTimeout(function () { reportMiss(q, 'no_match'); }, 900);
          html += '<div class="zvh-finder__none">No match for “' + esc(q.trim()) + '”. <a href="' + esc(D.whatsapp) + '" target="_blank" rel="noopener" data-wa>Ask us on WhatsApp →</a></div>';
        }
      }
      html += '<div class="zvh-finder__keys"><span>↑ ↓ to move</span><span>↵ to select</span><span>esc to close</span></div>';
      panel.innerHTML = html;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    function closePanel() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }
    input.addEventListener('focus', function () { clearTimeout(closeTimer); renderPanel(); zvaOnce('finder_open'); });
    input.addEventListener('input', function () { syncPlaceholder(); renderPanel(); if (input.value.trim()) zvaOnce('finder_typed'); });
    input.addEventListener('blur', function () { closeTimer = setTimeout(closePanel, 180); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); if (panel.hidden) renderPanel(); else setActive(act + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(act - 1); }
      else if (e.key === 'Enter') { e.preventDefault(); if (!panel.hidden && list[act]) choose(list[act]); }
      else if (e.key === 'Escape') { closePanel(); input.blur(); }
    });
    bar.addEventListener('click', function (e) { if (!goBtn.contains(e.target)) input.focus(); });
    goBtn.addEventListener('click', function () {
      if (input.value.trim() && list[act] && !panel.hidden) choose(list[act]);
      else if (input.value.trim() && matches(input.value)[0]) choose(matches(input.value)[0]);
      else input.focus();
    });
    /* keep the caret in the box while the list is used with a mouse */
    panel.addEventListener('mousedown', function (e) { e.preventDefault(); });
    panel.addEventListener('mousemove', function (e) {
      var row = e.target.closest && e.target.closest('.zvh-finder__opt');
      if (row) { var i = +row.getAttribute('data-i'); if (i !== act) setActive(i); }
    });
    panel.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : e.target.parentNode;
      var row = t.closest('.zvh-finder__opt'), brand = t.closest('[data-brand]'), wa = t.closest('[data-wa]');
      if (row) { var c = list[+row.getAttribute('data-i')]; if (c) choose(c); }
      else if (brand) {
        clearTimeout(closeTimer);
        input.value = brand.getAttribute('data-brand') + ' ';
        syncPlaceholder();
        input.focus();
        renderPanel();
      } else if (wa) reportMiss(input.value, 'wa_click');
    });

    /* ---- recommendation ---- */
    function deliverable(c) {
      if (c.phase === 'three' && state.supply !== 'three_phase') return DERATE[c.kw] || Math.round((c.kw / 3) * 10) / 10;
      return c.kw;
    }
    function realKw(car, c) { return Math.round(Math.min(car.o, deliverable(c), SUPPLY_KW[state.supply]) * 10) / 10; }
    function model(car) {
      var pref = state.pref;
      var stocked = CHARGERS.filter(function (c) { return c.av !== 0; });
      var all = stocked.length ? stocked : CHARGERS.slice();
      var ceiling = car.m || 0;
      var inClass = function (c) { return ceiling ? c.kw <= ceiling + 0.01 : c.kw - car.o < POWER_CLASS_KW; };
      var top = car.t || [];
      var rankOf = function (c) { var i = top.indexOf(String(c.id)); return i === -1 ? 999 : i; };
      var ranked = function (c) { return rankOf(c) < 999; };
      /* a featured charger, then what owners of this car buy most, then the dearer one */
      var order = function (a, b) { return (b.feat || 0) - (a.feat || 0) || rankOf(a) - rankOf(b) || b.price - a.price; };
      /* the chargers of a pool this car can use at its speed on the chosen supply, best first */
      var fitting = function (pool) {
        var usable = pool.filter(function (c) { return !(c.phase === 'three' && state.supply !== 'three_phase'); });
        if (!usable.length) usable = pool;
        var cands = usable.filter(function (c) { return c.kw >= car.o - 0.5; });
        if (!cands.length) cands = usable;
        var classCands = cands.filter(inClass);
        return (classCands.length ? classCands : cands).slice().sort(order);
      };
      var typed = pref ? all.filter(function (c) { return c.type === pref; }) : [];
      var fit = fitting(typed.length ? typed : all);
      var pick = fit[0];
      if (!pick) return {};
      var kw = realKw(car, pick), rest = fit.slice(1), alts = [];
      /* first row: the runner-up. Second row: the best of another kind than the
         pick, unless the runner-up already is one (then simply the next). */
      var second = rest[0];
      var third = (second && !pref && second.type === pick.type && rest.filter(function (c) { return c.type !== pick.type; })[0]) || rest[1];
      [second, third].forEach(function (c) { if (c) alts.push(c); });
      /* a kind was asked for and it has fewer than three that fit: the rows left go to the best of the other kinds */
      if (typed.length && alts.length < 2) fitting(all).forEach(function (c) { if (alts.length < 2 && c !== pick && alts.indexOf(c) === -1) alts.push(c); });
      /* single phase gives this car less than it can take */
      var held = state.supply === 'single_phase' && kw < car.o - 0.5;
      var label = 'Best fit for your car', short = label;
      if (!pick.feat && ranked(pick)) {
        if (pref) label = short = 'Most chosen ' + KIND[pref];
        else if (car.tb === 'car') {
          short = 'Most chosen for your car';
          label = car.s && car.s.length <= LONG_NAME ? 'Most chosen by ' + car.s + ' owners' : short;
        } else label = short = held ? 'Best fit on single phase' : 'Most chosen for ' + (car.m || car.o) + ' kW cars';
      }
      return {
        pick: pick, alts: alts, kw: kw, label: label, short: short, held: held,
        hrs: kw > 0 && car.b > 0 ? car.b / kw : 0,
        kmh: car.b > 0 && car.r > 0 ? kw * car.r / car.b : 0
      };
    }
    function lineFor(c) { return [TYPE[c.type], c.spec].filter(Boolean).join(' · '); }
    function priceFor(c, short) { return (c.pv && !short ? 'From ' : '') + inr(c.price); }

    /* ---- result ---- */
    function seg(key, opts, value, extra) {
      var i = -1;
      opts.forEach(function (o, k) { if (o[0] === value) i = k; });
      return '<div class="zvh-seg' + (i < 0 ? ' is-empty' : '') + (extra || '') + '" data-seg="' + key + '" style="--n:' + opts.length + ';--i:' + Math.max(0, i) + '">'
        + '<div class="zvh-seg__thumb" aria-hidden="true"></div>'
        + opts.map(function (o) { return '<button type="button" data-v="' + o[0] + '"' + (o[0] === value ? ' class="is-on"' : '') + '>' + o[1] + '</button>'; }).join('')
        + '</div>';
    }
    var ARC = '<svg viewBox="0 0 320 170" aria-hidden="true"><defs>'
      + '<linearGradient id="zvArcG" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#54b474" stop-opacity=".35"/><stop offset=".22" stop-color="#6cc58a"/><stop offset=".6" stop-color="#a6dcbb"/><stop offset="1" stop-color="#a6dcbb" stop-opacity=".25"/></linearGradient>'
      + '<mask id="zvArcM" maskUnits="userSpaceOnUse" x="-20" y="-20" width="360" height="210"><path data-arc d="M 10 165 A 150 150 0 0 1 310 165" fill="none" stroke="#fff" stroke-width="16" stroke-dasharray="471.3" stroke-dashoffset="471.3"/></mask></defs>'
      + '<path d="M 10 165 A 150 150 0 0 1 310 165 L 309.2 165 A 149.2 144 0 0 0 10.8 165 Z" fill="rgba(255,255,255,.07)"/>'
      + '<path d="M 10 165 A 150 150 0 0 1 310 165 L 309.2 165 A 149.2 144 0 0 0 10.8 165 Z" fill="url(#zvArcG)" mask="url(#zvArcM)"/></svg>';

    function answersHtml(car, m) {
      var pk = m.pick;
      var note = m.alts.length ? 'That’s every charger that fits.' : 'No other charger fits your car.';
      var web = '<div class="zvh-cf__swap">'
        + '<p class="zvh-cf__insight">' + (m.held
          ? 'On single phase your ' + esc(car.n) + ' charges at up to <strong>' + esc(m.kw) + ' kW</strong>.'
          : 'Your ' + esc(car.n) + ' supports up to <strong>' + esc(car.o) + ' kW</strong> AC charging.') + '</p>'
        + '<div class="zvh-cf__card"><div class="zvh-cf__pick">'
        + '<span class="zvh-cf__pickimg">' + (pk.img ? '<img src="' + esc(pk.img) + '" alt="' + esc(pk.name) + '">' : '') + '</span>'
        + '<div class="zvh-cf__pickbody"><span class="zvh-eyebrow">' + esc(m.label) + '</span>'
        + '<div class="zvh-cf__pickrow"><a class="zvh-cf__pickname" href="' + esc(pk.url) + '" data-buy>' + esc(pk.name) + '</a><span class="zvh-cf__pickprice">' + priceFor(pk) + '</span></div>'
        + '<span class="zvh-cf__pickline">' + esc(lineFor(pk)) + '</span></div></div>'
        + '<a class="zvh-cf__buy" href="' + esc(pk.url) + '" data-buy><span>Buy ' + esc(pk.name) + '</span><i>→</i></a>'
        + '<div class="zvh-cf__alts"><span class="zvh-cf__altsh">Also fits your car</span>';
      var mob = '<div class="zvh-cfm__sheet"><span class="zvh-cfm__grab"></span><div class="zvh-cfm__pick">'
        + '<span>' + (pk.img ? '<img src="' + esc(pk.img) + '" alt="' + esc(pk.name) + '">' : '') + '</span>'
        + '<span><span class="zvh-cfm__badge">' + esc(m.short) + '</span><a class="zvh-cfm__name" href="' + esc(pk.url) + '" data-buy>' + esc(pk.name) + '</a></span>'
        + '<a class="zvh-cfm__buy" href="' + esc(pk.url) + '" data-buy>' + priceFor(pk, true) + ' <i>→</i></a></div>'
        + '<div class="zvh-cfm__alts"><span class="zvh-cfm__altsh">Also fits your car</span>';
      for (var i = 0; i < 2; i++) {
        var a = m.alts[i];
        if (a) {
          web += '<a class="zvh-cf__alt" href="' + esc(a.url) + '" data-alt><span class="zvh-cf__altimg">' + (a.img ? '<img src="' + esc(a.img) + '" alt="">' : '') + '</span>'
            + '<span class="zvh-cf__altbody"><b>' + esc(a.name) + '</b><small>' + esc(lineFor(a)) + '</small></span><span class="zvh-cf__altprice">' + priceFor(a) + '</span></a>';
          mob += '<a class="zvh-cfm__alt" href="' + esc(a.url) + '" data-alt><span>' + (a.img ? '<img src="' + esc(a.img) + '" alt="">' : '') + '</span>'
            + '<span><b>' + esc(a.name) + '</b><small>' + esc(lineFor(a)) + '</small></span><em>' + priceFor(a, true) + '</em><i>›</i></a>';
        } else {
          var show = i === m.alts.length ? note : '';
          web += '<div class="zvh-cf__note">' + show + '</div>';
          mob += '<div class="zvh-cfm__note">' + show + '</div>';
        }
      }
      return { web: web + '</div></div></div>', mob: mob + '</div></div>' };
    }

    var anim = { raf: null, cur: null, key: null };
    function paintNumbers(h, k, a) {
      var hrs = (Math.round(h * 10) / 10).toFixed(1), km = '~' + Math.round(k);
      Array.prototype.forEach.call(result.querySelectorAll('[data-hrs]'), function (el) { el.textContent = hrs; });
      Array.prototype.forEach.call(result.querySelectorAll('[data-km]'), function (el) { el.textContent = km; });
      var arc = result.querySelector('[data-arc]');
      if (arc) arc.setAttribute('stroke-dashoffset', (471.3 * (1 - a)).toFixed(1));
    }
    function animateNumbers(car, m) {
      var key = car.n + '|' + m.kw;
      if (anim.key === key) { if (anim.cur) paintNumbers(anim.cur.h, anim.cur.k, anim.cur.a); return; }
      var fresh = !anim.cur, from = anim.cur || { h: 0, k: 0 }, to = { h: m.hrs, k: m.kmh };
      anim.key = key;
      cancelAnimationFrame(anim.raf);
      if (REDUCED) { anim.cur = { h: to.h, k: to.k, a: 1 }; paintNumbers(to.h, to.k, 1); return; }
      var t0 = performance.now() + (fresh ? 320 : 0);
      var cl = function (v) { return Math.max(0, Math.min(1, v)); }, eo = function (v) { return 1 - Math.pow(1 - v, 4); };
      (function step() {
        var now = performance.now();
        var eH = eo(cl((now - t0) / 1000)), eA = eo(cl((now - t0 - 120) / 1150)), eK = eo(cl((now - t0 - 260) / 1000));
        anim.cur = { h: from.h + (to.h - from.h) * eH, k: from.k + (to.k - from.k) * eK, a: eA };
        paintNumbers(anim.cur.h, anim.cur.k, anim.cur.a);
        if (now - t0 < 1500) anim.raf = requestAnimationFrame(step);
      })();
    }

    function renderResult(firstTime) {
      var car = state.car;
      if (!car) return;
      var m = model(car);
      if (!m.pick) return;
      var rk = [car.n, state.supply, state.pref, m.kw, m.pick.name, m.label, m.alts.map(function (a) { return a.name; }).join()].join('|');
      if (rk !== lastResultKey) {
        lastResultKey = rk;
        sendEvent('result', { car: car.n, supply: state.supply, pref: state.pref || null, kw: m.kw, pick: handleFromUrl(m.pick.url) });
      }
      var ans = answersHtml(car, m);
      var meta = esc(car.b) + ' kWh battery · ' + esc(car.o) + ' kW AC';
      var kwline = 'Charging at ' + m.kw + ' kW' + (m.kw === car.o ? ' · your car’s top AC speed' : '');
      var hint = state.supply === 'single_phase' ? 'Most homes are single phase.'
        : (m.kw > SUPPLY_KW.single_phase ? 'Three phase lets your car charge at ' + m.kw + ' kW.' : 'Your car charges at ' + m.kw + ' kW on either supply.');
      if (firstTime) {
        var disc = D.disclaimer ? '<details><summary>Indicative figures — verify before you buy.</summary><p>' + esc(D.disclaimer) + '</p></details>' : '<span>Indicative figures — verify before you buy.</span>';
        var ask = '<a href="' + esc(D.whatsapp) + '" target="_blank" rel="noopener" class="zv-wa-sales">Rather ask a person? WhatsApp us</a>';
        result.innerHTML =
          '<div class="zvh-cf zvh-web">'
          + '<div class="zvh-cf__left"><div class="zvh-cf__car"><span><b>' + esc(car.n) + '</b><small>' + meta + '</small></span><button type="button" class="zvh-cf__change" data-change>Change car</button></div>'
          + '<div class="zvh-cf__dial"><span class="zvh-cf__lbl">Charge from 0 to 100%</span><span class="zvh-cf__hrs"><span data-hrs>0.0</span><small>hrs</small></span>'
          + '<div class="zvh-cf__arc">' + ARC + '<div><span class="zvh-cf__lbl2">Range added<br>per hour</span><span class="zvh-cf__km"><span data-km>~0</span><small>km</small></span></div></div>'
          + '<span class="zvh-cf__kwline" data-kwline></span></div></div>'
          + '<div class="zvh-cf__right"><div class="zvh-cf__qs">'
          + '<div class="zvh-cf__q"><span class="zvh-cf__qt">What supply do you have at home?</span>' + seg('supply', [['single_phase', 'Single phase'], ['three_phase', 'Three phase']], state.supply) + '<span class="zvh-cf__hint" data-hint></span></div>'
          + '<div class="zvh-cf__q"><div class="zvh-cf__qrow"><span class="zvh-cf__qt">What kind of charger do you want?</span><span class="zvh-cf__hint">Optional</span></div>' + seg('pref', [['wall', 'Wall'], ['portable', 'Portable'], ['aio', 'All-in-one']], state.pref) + '</div>'
          + '</div><div class="zvh-cf__ans" data-ans-web></div>'
          + '<div class="zvh-cf__foot">' + disc + ask + '</div></div></div>'
          + '<div class="zvh-cfm zvh-mob"><div class="zvh-cfm__card"><div class="zvh-cfm__top">'
          + '<div class="zvh-cfm__car"><span><b>' + esc(car.n) + '</b><small data-upto>Up to ' + esc(car.o) + ' kW AC charging</small></span><button type="button" class="zvh-cf__change" data-change>Change</button></div>'
          + '<div class="zvh-cfm__nums"><div><b><span data-hrs>0.0</span><small>hrs</small></b><span>0 to 100% charge</span></div><div><b><span data-km>~0</span><small>km</small></b><span>Range per hour</span></div></div>'
          + '<div class="zvh-cfm__ctl"><div><span>Home supply</span>' + seg('supply', [['single_phase', 'Single'], ['three_phase', 'Three']], state.supply) + '</div>'
          + '<div><span>Type</span>' + seg('pref', [['wall', 'Wall'], ['portable', 'Portable'], ['aio', 'AIO']], state.pref) + '</div></div>'
          + '</div><div data-ans-mob></div></div>'
          + '<a class="zvh-cfm__ask zv-wa-sales" href="' + esc(D.whatsapp) + '" target="_blank" rel="noopener">Rather ask a person? WhatsApp us</a></div>';
      } else {
        Array.prototype.forEach.call(result.querySelectorAll('.zvh-seg'), function (s) {
          var key = s.getAttribute('data-seg'), val = state[key], idx = -1;
          Array.prototype.forEach.call(s.querySelectorAll('button'), function (b, k) {
            var on = b.getAttribute('data-v') === val;
            b.classList.toggle('is-on', on);
            if (on) idx = k;
          });
          s.classList.toggle('is-empty', idx < 0);
          if (idx >= 0) s.style.setProperty('--i', idx);
        });
      }
      result.querySelector('[data-ans-web]').innerHTML = ans.web;
      result.querySelector('[data-ans-mob]').innerHTML = ans.mob;
      result.querySelector('[data-kwline]').textContent = kwline;
      var upto = result.querySelector('[data-upto]');
      if (upto) upto.textContent = m.held ? 'Up to ' + m.kw + ' kW on single phase' : 'Up to ' + car.o + ' kW AC charging';
      result.querySelector('[data-hint]').textContent = hint;
      animateNumbers(car, m);
    }

    function choose(car) {
      clearTimeout(closeTimer);
      state.car = car;
      input.value = '';
      syncPlaceholder();
      closePanel();
      input.blur();
      anim.cur = null;
      anim.key = null;
      root.classList.add('has-car');
      result.hidden = false;
      sendEvent('car_selected', { car: car.n });
      renderResult(true);
      setTimeout(function () {
        var top = root.getBoundingClientRect().top + window.scrollY - (window.innerWidth < 750 ? 64 : 70);
        if (Math.abs(window.scrollY - top) > 40) window.scrollTo({ top: top, behavior: REDUCED ? 'auto' : 'smooth' });
      }, 80);
    }
    function reset() {
      state.car = null;
      root.classList.remove('has-car');
      result.hidden = true;
      result.innerHTML = '';
      cancelAnimationFrame(anim.raf);
      input.value = '';
      syncPlaceholder();
    }
    result.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target : e.target.parentNode;
      var segBtn = t.closest('.zvh-seg button'), buy = t.closest('[data-buy]'), alt = t.closest('[data-alt]');
      if (t.closest('[data-change]')) { reset(); setTimeout(function () { input.focus(); }, 30); return; }
      if (segBtn) {
        var key = segBtn.parentNode.getAttribute('data-seg'), v = segBtn.getAttribute('data-v');
        if (key === 'supply') { if (state.supply === v) return; state.supply = v; }
        else state.pref = state.pref === v ? '' : v;   /* tapping the chosen type clears it */
        renderResult(false);
        return;
      }
      if (buy || alt) markCart(buy || alt, !!buy);
      if (buy) sendEvent('buy_click', { car: state.car && state.car.n, target: handleFromUrl(buy.href) });
      else if (alt) sendEvent('alt_click', { car: state.car && state.car.n, target: handleFromUrl(alt.href) });
    });

    /* ---- start ---- */
    renderPopular();
    try {
      var seen = false;
      new IntersectionObserver(function (entries, obs) {
        if (!seen && entries.some(function (en) { return en.isIntersecting; })) { seen = true; sendEvent('view', {}); obs.disconnect(); }
      }, { threshold: 0.4 }).observe(root);
    } catch (e) {}

    /* Prefill only from an explicit ?ev= sales link — never a stored default. */
    (function () {
      var slug = null;
      try { slug = new URLSearchParams(window.location.search).get('ev'); } catch (e) {}
      if (!slug) return;
      slug = SLUG_ALIASES[slug] || slug;
      var car = CARS.filter(function (c) { return slugify(c.n) === slug; })[0];
      if (car) choose(car);
    })();

    /* Live refresh from the public cars feed: the same rows as the metafield,
       in the short form finder_cars_sync.finder_row writes. */
    try {
      fetch(CARS_FEED).then(function (r) { return r.json(); }).then(function (d) {
        var feed = Array.isArray(d) ? d : (d.cars || []);
        if (!feed.length) return;
        CARS = feed.map(function (c) {
          return { n: (c.brand + ' ' + c.model).trim(), o: +c.onboard_charger_kw || 7.5, m: +c.max_charger_kw || 0, b: +c.battery_kwh || 0, r: +c.range_km || 0, p: !!c.popular, k: c.keywords || '', s: c.model || '', t: (c.top || []).map(String), tb: c.top_by || '' };
        });
        renderPopular();
        if (state.car) {
          var again = findCar(state.car.n);
          if (!again) reset();
          else { state.car = again; renderResult(false); }
        }
      }).catch(function () {});
    } catch (e) {}
  }

  window.__zvhFinderInit = init;
  if (window.ZVH) { window.ZVH.init.finder = init; window.ZVH.mount(); }
})();
